import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  Signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import {
  TuiButton,
  TuiDialogContext,
  TuiHint,
  TuiLoader,
} from '@taiga-ui/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FileUrlPipe } from '@features/requests/request-form/components/request-form-workflows/workflow-wizard/components/form-fields/input-fields/input-photos/pipes/file-url.pipe';
import { filter, Subscriber, take } from 'rxjs';
import { DeviceDetectorService, XAlertService } from '@core/services';
import { InputPhotosService } from '@core/services/workflow/input-photos.service';
import {
  FileItem,
  PhotoStatus,
} from '@core/services/workflow/input-photos.types';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { TuiPreview, TuiPreviewDialogService } from '@taiga-ui/kit';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tuiClamp, TuiSwipe, TuiSwipeEvent } from '@taiga-ui/cdk';
import { WorkflowFormFieldInputPhotos } from '@workflows/types';
import {
  getWorkflowFileConstraints,
  type PhotoQueueTarget,
} from '@core/services/workflow';
import { DismissMobileHintDirective } from '@common/directives/dismiss-mobile-hint.directive';

@Component({
  selector: 'x-input-photos-preview',
  imports: [
    DismissMobileHintDirective,
    TuiButton,
    TuiHint,
    ReactiveFormsModule,
    FileUrlPipe,
    AsyncPipe,
    TuiLoader,
    TranslatePipe,
    TuiPreview,
    TuiSwipe,
  ],
  templateUrl: './input-photos-preview.component.html',
  styleUrl: './input-photos-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputPhotosPreviewComponent {
  field = input<WorkflowFormFieldInputPhotos | null>();
  /** Экземпляр поля: заявка, регламент заявки, нода, форма и ключ поля */
  target = input<PhotoQueueTarget | null>(null);
  canExecuteWorkflow = input<boolean>(false);
  photoSelected = output<File[]>();
  photosSync = output<void>();

  @ViewChild('imagePreview')
  readonly imagePreview?: TemplateRef<TuiDialogContext>;

  private readonly inputPhotosService = inject(InputPhotosService);
  public readonly deviceService = inject(DeviceDetectorService);
  private readonly previewDialogService = inject(TuiPreviewDialogService);
  private readonly destroyRef = inject(DestroyRef);

  currentPreviewIndex = 0;

  private fileInput =
    viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  protected photoFiles: Signal<FileItem[]> = computed(() => {
    const target = this.target();

    return target ? this.inputPhotosService.getTargetFiles(target) : [];
  });

  /** accept выбора файла из схемы (по умолчанию — изображения) */
  protected readonly $accept = computed(() => {
    const field = this.field();

    return field
      ? getWorkflowFileConstraints(field, 'image/*').accept
      : 'image/*';
  });

  protected previewLength = computed(() => this.photoFiles().length);

  private readonly xAlertService = inject(XAlertService);

  selectFiles() {
    if (!this.canExecuteWorkflow()) {
      return;
    }

    this.fileInput().nativeElement.click();
  }

  onFileSelected(event: Event) {
    if (!this.canExecuteWorkflow()) {
      return;
    }

    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);

      this.photoSelected.emit(newFiles);
    }

    // Тот же файл можно выбрать повторно
    input.value = '';
  }

  confirmRemoveFile(photo: FileItem) {
    if (!this.canExecuteWorkflow()) {
      return;
    }

    this.xAlertService
      .showDialog(
        'Подтверждение удаления',
        `Вы уверены, что хотите удалить файл <b>${photo.name || photo.path}</b>?`,
      )
      .pipe(
        filter(confirmed => Boolean(confirmed)),
        take(1),
      )
      .subscribe(() => {
        this.inputPhotosService.deleteFile(photo);
      });
  }

  sync() {
    this.photosSync.emit();
  }

  retryUpload(photo: FileItem): void {
    this.inputPhotosService.retryFileUpload(photo);
  }

  shouldShowLoader(status: PhotoStatus): boolean {
    return status === 'uploading' || status === 'deleting';
  }

  isQueuedOfflineUpload(status: PhotoStatus): boolean {
    return status === 'queued_upload';
  }

  isQueuedOfflineRemove(status: PhotoStatus): boolean {
    return status === 'queued_remove';
  }

  canOpenPreview(status: PhotoStatus): boolean {
    return status === 'completed';
  }

  canRemoveFile(status: PhotoStatus): boolean {
    return (
      status === 'completed' ||
      status === 'new' ||
      status === 'upload_error' ||
      status === 'queued_upload'
    );
  }

  retryRemove(photo: FileItem): void {
    this.inputPhotosService.retryFileRemove(photo);
  }

  dismissRemoveError(photo: FileItem): void {
    this.inputPhotosService.dismissRemoveError(photo);
  }

  showPreview(index: number): void {
    this.currentPreviewIndex = index;

    this.previewDialogService
      .open(this.imagePreview || '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  onPhotoClick(index: number, status: PhotoStatus): void {
    if (this.canOpenPreview(status)) {
      this.showPreview(index);
    }
  }

  onSwipePreview(swipe: TuiSwipeEvent): void {
    if (swipe.direction === 'left') {
      this.currentPreviewIndex = tuiClamp(
        this.currentPreviewIndex + 1,
        0,
        this.previewLength() - 1,
      );
    }

    if (swipe.direction === 'right') {
      this.currentPreviewIndex = tuiClamp(
        this.currentPreviewIndex - 1,
        0,
        this.previewLength() - 1,
      );
    }
  }

  closePreview(observer: Subscriber<unknown>): void {
    observer.complete();
  }

  backdropClickClose(event: Event, observer: Subscriber<unknown>): void {
    if ((event.target as HTMLElement).tagName === 'TUI-PREVIEW') {
      this.closePreview(observer);
    }
  }

  get currentFile() {
    return this.photoFiles()[this.currentPreviewIndex];
  }

  get currentFileName() {
    const file = this.currentFile;

    return file.name || file.path;
  }
}
